<template>
  <nav class="pagination" :aria-label="ariaPaginationLabel">
    <div class="pagination-previous">
      <component
        :is="current === 1 ? 'div' : NuxtLink"
        :to="current === 1 ? '' : previousPage"
        :aria-label="ariaPreviousPageLabel"
        :disabled="current === 1 ? 'true' : undefined"
        class="button is-small"
      >
        <SpriteSymbol name="chevron-left" />
        <span>{{ $texts('prev') }}</span>
      </component>
    </div>
    <div class="pagination-next">
      <component
        :is="current === total ? 'div' : NuxtLink"
        :to="current === total ? '' : nextPage"
        :aria-label="ariaNextPageLabel"
        :disabled="current === total ? 'true' : undefined"
        class="button is-small"
      >
        <span>{{ $texts('next') }}</span>
        <SpriteSymbol name="chevron-right" />
      </component>
    </div>
    <ul class="pagination-list">
      <li v-for="page in beginningPages" :key="page">
        <Page :page="page" :is-current="page === current" />
      </li>
      <li v-if="hasBeginningGap" aria-hidden="true">&hellip;</li>
      <li
        v-for="page in slidingWindowPages"
        :key="page"
        :class="[
          classMap.element,
          isCurrentPage(page) ? classMap.elementActive : '',
        ]"
      >
        <Page :page="page" :is-current="page === current" />
      </li>
      <li v-if="hasEndingGap" aria-hidden="true">&hellip;</li>
      <li
        v-for="page in endingPages"
        :key="page"
        :class="[
          classMap.element,
          isCurrentPage(page) ? classMap.elementActive : '',
        ]"
      >
        <Page :page="page" :is-current="page === current" />
      </li>
    </ul>
  </nav>
</template>

<script lang="ts" setup>
import Page from './Page.vue'
import { NuxtLink } from '#components'

const { $texts } = useEasyTexts()
const route = useRoute()

const props = withDefaults(
  defineProps<{
    ariaPaginationLabel?: string
    ariaGotoPageLabel?: string
    ariaPreviousPageLabel?: string
    ariaNextPageLabel?: string
    ariaCurrentPageLabel?: string
    classMap?: Record<string, string>
    current: number
    total: number
    slidingEndingSize?: number
    slidingWindowSize?: number
    nonSlidingSize?: number
    pageComponent?: string
  }>(),
  {
    ariaPaginationLabel: 'Pagination Navigation',
    ariaGotoPageLabel: 'Go to page %page% of %total%',
    ariaPreviousPageLabel: 'Go to previous page',
    ariaNextPageLabel: 'Go to next page',
    ariaCurrentPageLabel: 'Page %page% of %total%, current page',
    classMap: () => ({}),
    slidingEndingSize: 2,
    slidingWindowSize: 3,
    nonSlidingSize: 9,
    pageComponent: 'sliding-pagination-default-page',
  },
)

function range(start: number, end: number) {
  const r: number[] = []

  if (typeof start !== 'number' || typeof end !== 'number') {
    return r
  }

  if (start > end) {
    const temp = start
    start = end
    end = temp
  }

  for (let i = start; i <= end; i++) {
    r.push(i)
  }

  return r
}

const previousPage = computed(() => {
  const page = props.current - 2
  return {
    query: {
      ...route.query,
      page: page === 0 ? undefined : page.toString(),
    },
  }
})

const nextPage = computed(() => {
  return {
    query: {
      ...route.query,
      page: props.current.toString(),
    },
  }
})

const isSliding = computed(() => props.total > props.nonSlidingSize)

const beginningPages = computed(() =>
  range(1, isSliding.value ? props.slidingEndingSize : props.total),
)

const lastBeginningPage = computed(
  () => beginningPages.value[beginningPages.value.length - 1],
)

const endingPages = computed(() => {
  if (!isSliding.value) {
    return []
  }
  return range(props.total - props.slidingEndingSize + 1, props.total)
})

const firstEndingPage = computed(() => endingPages.value[0])

const slidingWindowHalf = computed(() => {
  let half = props.slidingWindowSize / 2
  if (props.slidingWindowSize % 2 === 1) {
    half -= 0.5
  }
  return half
})

const slidingWindowPages = computed(() => {
  if (!isSliding.value || !lastBeginningPage.value || !firstEndingPage.value) {
    return []
  }

  const startOffset = lastBeginningPage.value + slidingWindowHalf.value
  const endOffset = firstEndingPage.value - slidingWindowHalf.value

  if (props.current <= startOffset) {
    return range(
      lastBeginningPage.value + 1,
      lastBeginningPage.value + props.slidingWindowSize,
    )
  }

  if (props.current > startOffset && props.current < endOffset) {
    let upperHalfForEvenWindowSizes = slidingWindowHalf.value
    if (props.slidingWindowSize % 2 === 0) {
      upperHalfForEvenWindowSizes /= 2
    }

    return range(
      -slidingWindowHalf.value + props.current,
      upperHalfForEvenWindowSizes + props.current,
    )
  }

  return range(
    firstEndingPage.value - props.slidingWindowSize,
    firstEndingPage.value - 1,
  )
})

const firstWindowPage = computed(() => slidingWindowPages.value[0])

const lastWindowPage = computed(
  () => slidingWindowPages.value[slidingWindowPages.value.length - 1],
)

const hasBeginningGap = computed(() => {
  if (!isSliding.value || !lastBeginningPage.value) {
    return false
  }
  return lastBeginningPage.value + 1 !== firstWindowPage.value
})

const hasEndingGap = computed(() => {
  if (!isSliding.value || !lastWindowPage.value) {
    return false
  }
  return lastWindowPage.value + 1 !== firstEndingPage.value
})

function isCurrentPage(page: number) {
  return props.current === page
}
</script>
